import { serverInfo } from "../config/serverConfig";
import { checkResponse } from "../utils/apiError";


import { apiFetch } from "./apiFetch";

export const GOOGLE_RECONNECT_REQUIRED = "GOOGLE_RECONNECT_REQUIRED";

function parseJsonSafe(resp) {
  return resp.json().catch(() => ({}));
}

/**
 * Get Google Calendar OAuth URL. Returns { url } or throws.
 */
export async function getGoogleCalendarAuthUrl(accountId, userId, returnUrl = "/TimeBlocking") {
  const resp = await apiFetch(serverInfo.serverUrl + "/googleCalendar/authUrl", {
    method: "POST",
    body: JSON.stringify({
      account_id: accountId,
      user_id: userId,
      return_url: returnUrl,
    }),
  });
  checkResponse(resp);
  const data = await resp.json();
  return data;
}

/**
 * Get connection status. Returns { connected: boolean, configured: boolean }.
 */
export async function getGoogleCalendarStatus(accountId, userId) {
  const resp = await apiFetch(serverInfo.serverUrl + "/googleCalendar/status", {
    method: "POST",
    body: JSON.stringify({ account_id: accountId, user_id: userId }),
  });
  checkResponse(resp);
  return resp.json();
}

/**
 * Disconnect Google Calendar for this user.
 */
export async function disconnectGoogleCalendar(accountId, userId) {
  const resp = await apiFetch(serverInfo.serverUrl + "/googleCalendar/disconnect", {
    method: "POST",
    body: JSON.stringify({ account_id: accountId, user_id: userId }),
  });
  checkResponse(resp);
  return resp.json();
}

/**
 * Fetch Google Calendar events in range. start/end are ISO date strings.
 * Returns array of { id, summary, start, end, description }.
 */
export async function getGoogleCalendarEvents(accountId, userId, startIso, endIso) {
  const resp = await apiFetch(serverInfo.serverUrl + "/googleCalendar/getEvents", {
    method: "POST",
    body: JSON.stringify({
      account_id: accountId,
      user_id: userId,
      start: startIso,
      end: endIso,
    }),
  });
  const data = await parseJsonSafe(resp);
  if (resp.status === 403 && data.code === GOOGLE_RECONNECT_REQUIRED) {
    const e = new Error(data.error || "Google Calendar reconnect required");
    e.code = GOOGLE_RECONNECT_REQUIRED;
    throw e;
  }
  checkResponse(resp);
  if (!resp.ok) {
    return [];
  }
  return Array.isArray(data) ? data : [];
}

/**
 * Pull event times from Google and update our time blocks to match (Google → App). Returns { updated: number }.
 */
export async function syncFromGoogle(accountId, userId, startIso, endIso) {
  const resp = await apiFetch(serverInfo.serverUrl + "/googleCalendar/syncFromGoogle", {
    method: "POST",
    body: JSON.stringify({
      account_id: accountId,
      user_id: userId,
      start: startIso,
      end: endIso,
    }),
  });
  const data = await parseJsonSafe(resp);
  if (resp.status === 403 && data.code === GOOGLE_RECONNECT_REQUIRED) {
    const e = new Error(data.error || "Google Calendar reconnect required");
    e.code = GOOGLE_RECONNECT_REQUIRED;
    throw e;
  }
  checkResponse(resp);
  if (!resp.ok) {
    throw new Error(data.error || "Failed to sync from Google");
  }
  return data;
}

/**
 * Create Google events for time blocks in range that don't have one yet (e.g. created before connecting).
 * Returns { synced: number }.
 */
export async function syncExistingBlocksToGoogle(accountId, userId, startIso, endIso) {
  const resp = await apiFetch(serverInfo.serverUrl + "/googleCalendar/syncExistingBlocks", {
    method: "POST",
    body: JSON.stringify({
      account_id: accountId,
      user_id: userId,
      start: startIso,
      end: endIso,
    }),
  });
  checkResponse(resp);
  return resp.json();
}
